import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import api, { getFreshAccessToken } from "../../chat/api";
import useProjectsStore from "../../../store/useProjectsStore";
import SuccessPopup from "../SuccessPopup";
import RenameProject from "./RenameProject";
import DeleteModal from "../KbFolder/DeleteModal";
import ShareProjectModal from "./ShareProjectModal";
import ProjectStatusModal from "./ProjectStatusModal";
import MyProjectDetailsModal from "./MyProjectDetailsModal";
import AddNotesModal from "./AddNotesModal";
import ArchiveProjectModal from "./ArchiveProjectModal";
import LeaveProjectModal from "./LeaveProjectModal";
import TransferOwnershipModal from "./TransferOwnershipModal";
import { showNoAccessToast } from "../../../utils/toast";
import { isSelectableRow, actionMenuItemCount, hasNoAccess } from "./projectAccess";

// Everything a ProjectDataTable row can do, shared by the Projects page and the dashboard.
// `selectedIds` / `onSelectionCleared` let a page with bulk selection reuse the delete flow.
export default function useProjectRowActions({ selectedIds = [], onSelectionCleared = () => {} } = {}) {
  const navigate = useNavigate();
  const projects = useProjectsStore((state) => state.projects);
  const setProjects = useProjectsStore((state) => state.setProjects);
  const fetchProjects = useProjectsStore((state) => state.fetchProjects);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isProjectDetailsModalOpen, setIsProjectDetailsModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const [renameLoading, setRenameLoading] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState("");

  const [projectToShare, setProjectToShare] = useState(null);
  const [projectToRename, setProjectToRename] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projectToUpdateStatus, setProjectToUpdateStatus] = useState(null);
  const [projectForDetails, setProjectForDetails] = useState(null);
  const [projectForNotes, setProjectForNotes] = useState(null);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [projectToLeave, setProjectToLeave] = useState(null);
  const [projectToTransfer, setProjectToTransfer] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState("");

  const [leaveStatus, setLeaveStatus] = useState("idle");

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0, openUpwards: false });
  const actionMenuRefs = useRef({});

  const [popupTitle, setPopupTitle] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteCompleted, setDeleteCompleted] = useState(false);

  // ─── Org members for the Project Lead dropdown ─────────────────────────────────
  // Fetched once per workspace and reused by every row's lead picker. Only
  // admins/owners may open the dropdown (canManageLead).
  const [orgMembers, setOrgMembers] = useState([]);
  const [canManageLead, setCanManageLead] = useState(false);

  useEffect(() => {
    let active = true;

    const loadMembers = async () => {
      try {
        const orgId = Cookies.get("selected_org_id");
        const email = (localStorage.getItem("email") || "").toLowerCase();
        if (!orgId) {
          if (active) { setOrgMembers([]); setCanManageLead(false); }
          return;
        }
        const res = await api.get(`/api/v1/org/members/list/?organization_id=${orgId}`);
        const members = Array.isArray(res.data) ? res.data : [];
        if (!active) return;
        setOrgMembers(members);
        const me = members.find((m) => (m.email || "").toLowerCase() === email);
        setCanManageLead(
          !!me &&
            (String(me.permission_role || "").toLowerCase() === "owner" ||
              String(me.role || "").toLowerCase() === "admin")
        );
      } catch {
        if (active) { setOrgMembers([]); setCanManageLead(false); }
      }
    };

    loadMembers();
    const onWorkspaceChange = () => loadMembers();
    window.addEventListener("workspace-changed", onWorkspaceChange);
    return () => {
      active = false;
      window.removeEventListener("workspace-changed", onWorkspaceChange);
    };
  }, []);

  // Members offered in the Project Lead dropdown — never include the current
  // user, since you can't share a project with yourself.
  const leadPickerMembers = useMemo(() => {
    const myEmail = (localStorage.getItem("email") || "").toLowerCase();
    return orgMembers.filter((m) => (m.email || "").toLowerCase() !== myEmail);
  }, [orgMembers]);

  const toggleActionMenu = (item, event) => {
    event.stopPropagation();
    event.preventDefault();

    // The menu's length depends on what the row lets this user do. Each item is
    // ~40px incl. gap, plus 16px of container padding; an oversight row shows a
    // short explanatory panel instead of a menu.
    const noAccess = hasNoAccess(item);
    const menuHeight = noAccess ? 96 : actionMenuItemCount(item) * 40 + 16;
    const menuWidth = noAccess ? 240 : 180;

    const rect = event.currentTarget.getBoundingClientRect();
    const bottomSpace = window.innerHeight - rect.bottom;
    const openUpwards = bottomSpace < menuHeight;

    setIsActionMenuOpen(isActionMenuOpen === item.id ? null : item.id);

    setActionMenuPosition({
      top: openUpwards ? rect.top - menuHeight - 8 : rect.bottom + 8,
      left: rect.right - menuWidth,
      openUpwards,
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isActionMenuOpen) {
        setIsActionMenuOpen(null);
      }
    };
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isActionMenuOpen]);

  const handleRowClick = (item) => {
    // Personal folders shared into the list can have no organization, in which
    // case the org-scoped route would resolve to ".../org/null/...".
    const path = item.org_id
      ? `/rfp-projects/project/${item.id}/org/${item.org_id}/project-overview`
      : `/rfp-projects/project/${item.id}/project-overview`;
    navigate(path, {
      state: {
        folderId: item.id,
        folderName: item.name,
        orgId: item.org_id,
      },
    });
  };

  const handleClickOutside = useCallback((event) => {
    if (
      isActionMenuOpen !== null &&
      actionMenuRefs.current[isActionMenuOpen] &&
      !actionMenuRefs.current[isActionMenuOpen].contains(event.target)
    ) {
      setIsActionMenuOpen(null);
    }
  }, [isActionMenuOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const showPopup = (title, message) => {
    setPopupTitle(title);
    setPopupMessage(message);
    setPopupVisible(true);
  };

  const handleRenameProject = async (newName) => {
    if (!projectToRename?.id) return showPopup("Error", "No project selected.");
    setRenameLoading(true);
    try {
      const token = await getFreshAccessToken();
      const response = await api.put(
        `/api/v1/readpdf/rename-folder/${projectToRename.id}/`,
        { new_name: newName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200 || response.status === 201) {
        setSuccessMessage(`Project renamed to "${newName}"`);
        setShowSuccessPopup(true);
        setIsRenameModalOpen(false);
        setProjectToRename(null);
        await fetchProjects(true);
      }
    } catch (error) {
      showPopup("Error", "Failed to rename project.");
    } finally {
      setRenameLoading(false);
    }
  };

  // `projectToDelete` is a snapshot taken when the confirm dialog opens — one row from
  // the row menu, or the checkbox selection from the bulk toolbar. The dialog's count
  // and the request both read it, so the number the user confirms is the number sent.
  // (The row-menu Delete used to be ignored whenever anything was checked, and the
  // whole selection was deleted instead.)
  const pendingDeleteIds = projectToDelete?.folder_ids || [];
  const isBulkDelete = Boolean(projectToDelete?.bulk);

  const deleteCount = pendingDeleteIds.length;
  const deleteTitle = `Delete ${deleteCount} project${deleteCount === 1 ? "" : "s"}?`;
  const deleteMessage = deleteCount === 1
    ? "The project and all its documents will be permanently deleted. This can't be undone."
    : "These projects and all their documents will be permanently deleted. This can't be undone.";

  const confirmDelete = async () => {
    const payload = { folder_ids: pendingDeleteIds };

    if (!payload.folder_ids.length) {
      setConfirmDeleteVisible(false);
      return showPopup("Error", "No items selected.");
    }

    setDeleteInProgress(true);
    setDeleteCompleted(false);

    try {
      const token = await getFreshAccessToken();
      await api.delete("/api/v1/rfp/folders/delete/", {
        data: { folder_ids: payload.folder_ids },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setProjects((prev) => prev.filter((p) => !payload.folder_ids.includes(p.id)));
      setDeleteCompleted(true);
      setDeleteInProgress(false);

      setTimeout(() => {
        setConfirmDeleteVisible(false);
        setProjectToDelete(null);
        fetchProjects(true);
      }, 2000);

      // A single-row delete leaves the checkbox selection alone; MyProjects drops the
      // deleted id from it on its own once the row disappears.
      if (isBulkDelete) onSelectionCleared();
      setSuccessMessage(`Successfully deleted ${payload.folder_ids.length} project(s)`);
      setShowSuccessPopup(true);
      setDeleteCompleted(false);
    } catch (error) {
      setDeleteInProgress(false);
      showPopup("Error", "Failed to delete projects.");
    }
  };

  const handleStatusSuccess = (updatedProjectId, newStatus) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === updatedProjectId ? { ...p, status: newStatus } : p))
    );
    fetchProjects(true);
  };

  const handleUpdateContractValue = async (folderId, newValue) => {
    const cleanValue = newValue.replace(/[^0-9.]/g, "");
    try {
      const token = await getFreshAccessToken();
      await api.patch(
        `/api/v1/rfp/folders/${folderId}/update-field/?type=contract_value`,
        { value: cleanValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProjects((prev) =>
        prev.map((p) => (p.id === folderId ? { ...p, contract_value: cleanValue || "_" } : p))
      );

      fetchProjects(true);
    } catch (error) {
      showPopup("Error", "Failed to update contract value.");
    }
  };

  const handleUpdateDeadline = async (folderId, newDate) => {
    try {
      const token = await getFreshAccessToken();
      await api.patch(
        `/api/v1/rfp/folders/${folderId}/update-field/?type=deadline`,
        { value: newDate },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProjects((prev) =>
        prev.map((p) => (p.id === folderId ? { ...p, deadline: newDate || "_" } : p))
      );

      fetchProjects(true);
    } catch (error) {
      showPopup("Error", "Failed to update Bid End Date.");
    }
  };

  // ─── Date Submitted: manual date, stored directly on the folder ────────────────
  const handleUpdateDateSubmitted = async (folderId, newDate) => {
    try {
      const token = await getFreshAccessToken();
      await api.patch(
        `/api/v1/rfp/folders/${folderId}/update-field/?type=date_submitted`,
        { value: newDate },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProjects((prev) =>
        prev.map((p) => (p.id === folderId ? { ...p, date_submitted: newDate || "_" } : p))
      );

      fetchProjects(true);
    } catch (error) {
      showPopup("Error", "Failed to update Date Submitted.");
    }
  };

  // ─── Project Lead: stored directly on the folder ───────────────────────────────
  const handleUpdateProjectLead = async (folderId, newValue) => {
    try {
      const token = await getFreshAccessToken();
      await api.patch(
        `/api/v1/rfp/folders/${folderId}/update-field/?type=project_lead`,
        { value: newValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProjects((prev) =>
        prev.map((p) => (p.id === folderId ? { ...p, project_lead: newValue || null } : p))
      );

      fetchProjects(true);
    } catch (error) {
      showPopup("Error", "Failed to update Project Lead.");
    }
  };

  // Picking a member from the Project Lead dropdown shares the project with them
  // and records them as the displayed lead. A legacy "member" role shares as "editor".
  const handleSetLeadMember = async (item, member) => {
    if (!item?.id || !member) return;
    try {
      const token = await getFreshAccessToken();
      // Org roles (Owner/Admin/Member) all share as "editor"; only reviewer stays read-only.
      const role = String(member.permission_role || "").toLowerCase();
      const permission = role === "reviewer" ? "reviewer" : "editor";

      await api.post(
        `/api/v1/org/folders/${item.id}/share/`,
        {
          organization_id: [member.organization_id],
          user_ids: [member.user_id],
          message: `Collaborating on ${item.name}`,
          permission: [permission],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // If a different member was already the project lead, unshare the project
      // from them now that the lead has changed.
      const prevLead = String(item.project_lead || "").trim();
      if (prevLead) {
        const prevMember = orgMembers.find(
          (m) => (m.full_name || m.email) === prevLead
        );
        if (prevMember && prevMember.user_id !== member.user_id) {
          await api.post(
            `/api/v1/org/folders/${item.id}/remove-share/`,
            { user_id: prevMember.user_id },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        }
      }

      // Reflect the chosen member as the project lead in the table.
      await handleUpdateProjectLead(item.id, member.full_name || member.email);
    } catch (error) {
      showPopup("Error", "Failed to set project lead.");
    }
  };

  const handleActionShare = (item) => {
    setIsActionMenuOpen(null);
    setProjectToShare(item);
    setIsShareModalOpen(true);
  };

  const handleActionDetails = (item) => {
    setIsActionMenuOpen(null);
    setProjectForDetails(item);
    setIsProjectDetailsModalOpen(true);
  };

  const handleActionRename = (item) => {
    setIsActionMenuOpen(null);
    setProjectToRename(item);
    setIsRenameModalOpen(true);
  };

  const handleActionDelete = (item) => {
    setIsActionMenuOpen(null);
    setProjectToDelete({ folder_ids: [item.id] });
    setConfirmDeleteVisible(true);
  };

  const handleActionNotes = (item) => {
    setIsActionMenuOpen(null);
    setProjectForNotes(item);
    setIsNotesModalOpen(true);
  };

  const handleSaveNote = async (note) => {
    const folderId = projectForNotes?.id;
    if (!folderId) return;
    setNoteSaving(true);
    try {
      const token = await getFreshAccessToken();
      await api.patch(
        `/api/v1/rfp/folders/${folderId}/update-field/?type=note`,
        { value: note },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProjects((prev) =>
        prev.map((p) => (p.id === folderId ? { ...p, note: note || null } : p))
      );

      setIsNotesModalOpen(false);
      setProjectForNotes(null);
      setSuccessMessage("Note saved.");
      setShowSuccessPopup(true);
      fetchProjects(true);
    } catch (error) {
      showPopup("Error", "Failed to save note.");
    } finally {
      setNoteSaving(false);
    }
  };

  const handleActionArchive = (item) => {
    setIsActionMenuOpen(null);
    setProjectToArchive(item);
    setArchiveError("");
    setIsArchiveModalOpen(true);
  };

  const handleConfirmArchive = async (reason) => {
    const folderId = projectToArchive?.id;
    if (!folderId) return;
    setArchiveLoading(true);
    setArchiveError("");
    try {
      const token = await getFreshAccessToken();
      await api.post(
        `/api/v1/rfp/folders/${folderId}/archive/`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProjects((prev) => prev.filter((p) => p.id !== folderId));
      setIsArchiveModalOpen(false);
      setProjectToArchive(null);
      setSuccessMessage(`"${projectToArchive.name}" was archived.`);
      setShowSuccessPopup(true);
      fetchProjects(true);
    } catch (error) {
      // Someone else may have just archived/deleted it, or permission changed —
      // either way the local list is stale, so refresh it once the modal closes.
      const message = error?.response?.data?.error || "Failed to archive project.";
      setArchiveError(message);
    } finally {
      setArchiveLoading(false);
    }
  };

  // ─── Org-admin oversight rows ──────────────────────────────────────────────────
  // An admin sees every member's project in this table, but sees them read-only
  // until the owner shares. Any interaction — opening the project, editing a
  // cell — explains that instead of failing silently or 403-ing on a dead page.
  const handleLockedAction = useCallback((item) => {
    showNoAccessToast(item?.owner?.name || item?.owner?.email);
  }, []);

  // ─── Transfer ownership ────────────────────────────────────────────────────────
  // Two ways in: the owner handing over, or an admin rescuing a project whose
  // owner has left the workspace (nobody can manage it until it changes hands).
  const handleActionTransferOwnership = (item) => {
    setIsActionMenuOpen(null);
    setProjectToTransfer(item);
    setTransferError("");
    setIsTransferModalOpen(true);
  };

  const confirmTransferOwnership = async (newOwnerId) => {
    if (!projectToTransfer?.id || !newOwnerId) return;
    setTransferLoading(true);
    setTransferError("");
    try {
      const token = await getFreshAccessToken();
      await api.post(
        `/api/v1/rfp/folders/${projectToTransfer.id}/transfer-ownership/`,
        { user_id: newOwnerId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setIsTransferModalOpen(false);
      setProjectToTransfer(null);
      setSuccessMessage("Project ownership transferred.");
      setShowSuccessPopup(true);
      // Ownership changes what every row allows, so reload rather than patch.
      fetchProjects(true);
    } catch (error) {
      setTransferError(
        error?.response?.data?.error || "Failed to transfer ownership. Please try again."
      );
    } finally {
      setTransferLoading(false);
    }
  };

  // ─── Leave Project (shared rows only) ──────────────────────────────────────────
  // An owner can archive or delete a project; a collaborator can only remove
  // themselves from it. Moved over from the old Shared With Me tab.
  const handleActionLeave = (item) => {
    setIsActionMenuOpen(null);
    setProjectToLeave(item);
    setLeaveStatus("idle");
    setIsLeaveModalOpen(true);
  };

  const confirmLeaveProject = async () => {
    if (!projectToLeave?.id) return;
    setLeaveStatus("leaving");
    try {
      await api.post(`/api/v1/org/folders/${projectToLeave.id}/leave/`);
      setLeaveStatus("success");

      // Drop the row straight away, then reconcile with the server.
      setProjects((prev) => prev.filter((p) => p.id !== projectToLeave.id));
      setTimeout(() => {
        setIsLeaveModalOpen(false);
        setProjectToLeave(null);
        setLeaveStatus("idle");
        fetchProjects(true);
      }, 1200);
    } catch (error) {
      setLeaveStatus("idle");
      setIsLeaveModalOpen(false);
      setProjectToLeave(null);
      showPopup("Error", "Failed to leave project.");
    }
  };

  const tableProps = {
    handleRowClick,
    onUpdateStatus: (project) => {
      setProjectToUpdateStatus(project);
      setIsStatusModalOpen(true);
    },
    handleUpdateContractValue,
    onUpdateDeadline: handleUpdateDeadline,
    onUpdateDateSubmitted: handleUpdateDateSubmitted,
    onUpdateProjectLead: handleUpdateProjectLead,
    orgMembers: leadPickerMembers,
    canManageLead,
    onSetLeadMember: handleSetLeadMember,
    isActionMenuOpen,
    toggleActionMenu,
    actionMenuPosition,
    actionMenuRefs,
    handleActionShare,
    handleActionRename,
    handleActionDelete,
    handleActionDetails,
    handleActionNotes,
    handleActionArchive,
    handleActionLeave,
    handleActionTransferOwnership,
    onLockedAction: handleLockedAction,
  };

  const modals = (
    <>
      <ProjectStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => { setIsStatusModalOpen(false); setProjectToUpdateStatus(null); }}
        projectId={projectToUpdateStatus?.id}
        currentStatus={projectToUpdateStatus?.status}
        onSuccess={handleStatusSuccess}
      />

      <MyProjectDetailsModal
        isOpen={isProjectDetailsModalOpen}
        onClose={() => { setIsProjectDetailsModalOpen(false); setProjectForDetails(null); }}
        projectData={projectForDetails}
      />

      <AddNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => { setIsNotesModalOpen(false); setProjectForNotes(null); }}
        onSave={handleSaveNote}
        initialNote={projectForNotes?.note || ""}
        loading={noteSaving}
      />

      <TransferOwnershipModal
        isOpen={isTransferModalOpen}
        onClose={() => { if (!transferLoading) { setIsTransferModalOpen(false); setProjectToTransfer(null); setTransferError(""); } }}
        onConfirm={confirmTransferOwnership}
        project={projectToTransfer}
        members={orgMembers}
        loading={transferLoading}
        error={transferError}
      />

      <LeaveProjectModal
        isOpen={isLeaveModalOpen}
        onClose={() => { if (leaveStatus === "idle") { setIsLeaveModalOpen(false); setProjectToLeave(null); } }}
        onConfirm={confirmLeaveProject}
        loading={leaveStatus === "leaving"}
        success={leaveStatus === "success"}
        buttonText={leaveStatus === "leaving" ? "Leaving..." : leaveStatus === "success" ? "Left" : "Leave"}
      />

      <ArchiveProjectModal
        isOpen={isArchiveModalOpen}
        onClose={() => { if (!archiveLoading) { setIsArchiveModalOpen(false); setProjectToArchive(null); setArchiveError(""); } }}
        onConfirm={handleConfirmArchive}
        loading={archiveLoading}
        error={archiveError}
      />

      {popupVisible && (
        <div className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg w-[446px] p-6 text-center animate-[fadeIn_0.3s_ease-in-out]">
            <h2 className="text-[#171717] font-outfit text-base font-semibold mb-2">{popupTitle}</h2>
            <p className="text-[#71717A] font-outfit text-[15px] font-medium w-full mx-auto">{popupMessage}</p>
            <button
              onClick={() => setPopupVisible(false)}
              className="bg-[#C95EBE] hover:bg-[#a850a1] text-white font-outfit text-sm font-semibold px-5 py-2.5 rounded-md mt-6 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <DeleteModal
        isOpen={confirmDeleteVisible}
        onClose={() => {
          if (deleteInProgress) return;
          setConfirmDeleteVisible(false);
          setProjectToDelete(null);
        }}
        onDelete={confirmDelete}
        fileName={""}
        loading={deleteInProgress}
        deleteSuccess={deleteCompleted}
        title={deleteTitle}
        message={deleteMessage}
      />

      {showSuccessPopup && (
        <SuccessPopup message={successMessage} onClose={() => setShowSuccessPopup(false)} />
      )}

      <RenameProject
        isOpen={isRenameModalOpen}
        onClose={() => { setIsRenameModalOpen(false); setProjectToRename(null); }}
        onRename={handleRenameProject}
        fileName={projectToRename?.name || ""}
        loading={renameLoading}
      />

      <ShareProjectModal
        isOpen={isShareModalOpen}
        onClose={() => { setIsShareModalOpen(false); setProjectToShare(null); }}
        projectId={projectToShare?.id}
        projectName={projectToShare?.name}
        orgId={projectToShare?.org_id}
        onShareSuccess={() => fetchProjects(true)}
      />
    </>
  );

  return {
    tableProps,
    modals,
    showPopup,
    closeActionMenu: () => setIsActionMenuOpen(null),
    // Bulk delete: snapshot the checkbox selection (replacing any row-menu target).
    openDeleteConfirm: () => {
      // Belt and braces: only ever send ids the user actually owns. The checkboxes
      // already enforce this, but a stale selection must never turn into a 403
      // (or worse) against someone else's project.
      const ownedIds = new Set(projects.filter(isSelectableRow).map((p) => p.id));
      setProjectToDelete({
        folder_ids: selectedIds.filter((id) => id && id !== "_" && ownedIds.has(id)),
        bulk: true,
      });
      setConfirmDeleteVisible(true);
    },
    closeDeleteConfirm: () => setConfirmDeleteVisible(false),
  };
}
